import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { ArrowLeft, Mail, Phone, MapPin, Dog, CalendarDays, ClipboardList, CreditCard, StickyNote } from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get(`/clients/${id}/detail`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [id]);
  if (err) return <div className="text-center py-20 text-muted-foreground">Client not found.</div>;
  if (!d) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  const c = d.client;
  const hwRate = d.homework_summary.total ? Math.round(d.homework_summary.done / d.homework_summary.total * 100) : 0;

  return (
    <div>
      <Button variant="ghost" size="sm" className="gap-2 mb-4 -ml-2 text-muted-foreground" onClick={() => navigate("/clients")} data-testid="client-back"><ArrowLeft size={16} /> Back to Clients</Button>

      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{c.full_name}</h1>
          <div className="flex flex-wrap gap-4 mt-1.5 text-sm text-muted-foreground">
            {c.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {c.email}</span>}
            {c.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {c.phone}</span>}
            {c.location_name && <span className="flex items-center gap-1.5"><MapPin size={13} /> {c.location_name}</span>}
          </div>
        </div>
        <StatusBadge value={c.status} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Dog size={18} strokeWidth={1.5} /> Dogs ({d.dogs.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {d.dogs.length ? d.dogs.map((dog) => (
                <div key={dog.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60 hover:bg-muted/40 cursor-pointer" onClick={() => navigate("/dogs")} data-testid="client-dog">
                  <div><div className="font-medium text-sm">{dog.name}</div><div className="text-xs text-muted-foreground">{dog.breed} · {dog.trainer_name || "Unassigned"}</div></div>
                  <StatusBadge value={dog.current_level} />
                </div>
              )) : <p className="text-sm text-muted-foreground">No dogs.</p>}
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><CalendarDays size={18} strokeWidth={1.5} /> Bookings ({d.bookings.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {d.bookings.length ? d.bookings.map((b, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-border/60" data-testid="client-booking">
                  <div><div className="font-medium text-sm">{b.class_name}</div><div className="text-xs text-muted-foreground">{fmtDate(b.date)} · {b.time}</div></div>
                  <div className="flex items-center gap-2"><StatusBadge value={b.status} /><StatusBadge value={b.attendance_status} /></div>
                </div>
              )) : <p className="text-sm text-muted-foreground">No bookings.</p>}
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><ClipboardList size={18} strokeWidth={1.5} /> Homework</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {d.homework.length ? d.homework.map((h) => (
                <div key={h.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60">
                  <div><div className="text-sm font-medium">{h.title}</div><div className="text-xs text-muted-foreground">Due {fmtDate(h.due_date)}</div></div>
                  <StatusBadge value={h.status} />
                </div>
              )) : <p className="text-sm text-muted-foreground">No homework.</p>}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg">Homework Completion</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-sm mb-1.5"><span className="text-muted-foreground">Progress</span><span className="font-medium">{hwRate}%</span></div>
              <ProgressBar value={hwRate} className="h-2" />
              <div className="text-xs text-muted-foreground mt-1">{d.homework_summary.done} of {d.homework_summary.total} done</div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><CreditCard size={18} strokeWidth={1.5} /> Payments</CardTitle></CardHeader>
            <CardContent><div className="p-3 rounded-lg border border-dashed border-border text-sm text-muted-foreground">Billing coming with HV Payments.</div></CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><StickyNote size={18} strokeWidth={1.5} /> Notes</CardTitle></CardHeader>
            <CardContent><p className="text-sm">{c.notes || <span className="text-muted-foreground">No notes.</span>}</p></CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
